import { html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_POLICY_PANEL_ACCORDION_BUTTONS } from '../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../utils/translator.helper';
import { connect } from 'pwa-helpers';
import { RootState, store } from '@nirvana/redux/store';
import { getInvalidTreePaths } from '../../../../../endpoints/policyPanel/redux/actions';
import { selectPoliciesState } from '../../../../../endpoints/policyPanel/redux/slice';
import { PolicyAppState } from '../../../../../endpoints/policyPanel/redux/types';
import { PolicyPanelEvents } from '../../../../../endpoints/policyPanel/utils/events';
import { getSaveRestrictionMessage } from '../../../../../endpoints/policyPanel/utils/restrictions';
import { PolicySaveRestriction } from '../../../../../endpoints/policyPanel/redux/saveButtonSlice/types.save';
import {
  getCloneVisibility,
  getIsLoading,
  getIsSaving,
  getIsWaitingValidation,
  getSaveRestriction,
} from '../../../../../endpoints/policyPanel/redux/saveButtonSlice/actions.save';
import { classMap } from 'lit/directives/class-map.js';
import { ButtonColors, ButtonTypes } from '../../../../../../ui/gzButton/types';
import { ButtonColor } from '../../../../../../ui/gzIconButton/types';
import { FontSizes, FontWeights } from '../../../../../../types/common/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_ACCORDION_BUTTONS)
export class GzPolicyPanelAccordionButtonsWebComponent extends connect(store)(LitElement) {
  static get styles() {
    return styles;
  }

  @state()
  disabled = false;

  @state()
  showSpinner = false;

  @state()
  isCloneVisible = false;

  @state()
  infoBoxMessage = '';

  stateChanged(rootState: RootState) {
    const appState = selectPoliciesState(rootState);
    const invalidTreePaths = getInvalidTreePaths(appState);
    const isLoading = getIsLoading(appState);
    const isSaving = getIsSaving(appState);
    const isRestricted = getSaveRestriction(appState) !== PolicySaveRestriction.NONE;
    const isCloneVisible = getCloneVisibility(appState);
    const isWaitingValidation = getIsWaitingValidation(appState);

    const isValid = invalidTreePaths.length === 0;

    this.disabled = !isValid || isLoading || isSaving || isRestricted || isWaitingValidation;
    this.showSpinner = isSaving;
    this.isCloneVisible = isCloneVisible;

    if (isRestricted) {
      this.infoBoxMessage = getSaveRestrictionMessage(getSaveRestriction(appState));
    } else if (!isValid) {
      this.infoBoxMessage = translateFn('Invalid policy settings');
    } else {
      this.infoBoxMessage = '';
    }
  }

  render() {
    return html`
      <div class="bottom-buttons">
        <gz-button
          id="save-button"
          ?disabled="${this.disabled}"
          ?showSpinner="${this.showSpinner}"
          @click="${this.handleSaveButton}"
          >${translateFn('Save#preserve state')}</gz-button
        >
        <gz-button
          id="clone-button"
          type="${ButtonTypes.OUTLINE}"
          color="${ButtonColors.SECONDARY}"
          class="${classMap({ hidden: !this.isCloneVisible })}"
          @click="${this.handleCloneButton}"
          >${translateFn('Clone')}</gz-button
        >

        <gz-icon
          class="icon"
          size="24"
          name="ds-icon-notification_warning"
          color="${ButtonColor.WARNING}"
          class="${classMap({ hidden: !this.infoBoxMessage })}"
        ></gz-icon>
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.NORMAL}"
          class="infoText ${classMap({ hidden: !this.infoBoxMessage })}"
          >${this.infoBoxMessage}</gz-text
        >

        <div class="flex-auto"></div>
        <gz-button type="outline" color="secondary" id="cancel-button" @click="${this.handleCancelButton}"
          >${translateFn('Cancel')}</gz-button
        >
      </div>
    `;
  }

  private handleCloneButton() {
    this.dispatchEvent(
      new CustomEvent(PolicyPanelEvents.CLONE, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleSaveButton() {
    this.dispatchEvent(
      new CustomEvent(PolicyPanelEvents.PRE_SAVE, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleCancelButton() {
    this.dispatchEvent(
      new CustomEvent(PolicyPanelEvents.CANCEL, {
        detail: {},
        bubbles: true,
        composed: true,
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_UI_SETTINGS_BOTTOM_BUTTONS: GzPolicyPanelAccordionButtonsWebComponent;
  }
}
